import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Select,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import ReactSelect from "react-select";
import { buildGroupedOptions } from "../../utils/buildGroupedOptions";
import { selectStyles } from "../../utils/selectStyel";

export default function ConditionEditor({
  sectionKey,
  condition,
  onUpdate,
  onDelete,
  mainSchemaData,
  fieldKey,
}) {
  // Helper: recursively initialize a group
  const initializeGroup = (group) => {
    const g = { ...group };
    if (!g.Field && fieldKey) g.Field = `${fieldKey}_CONDITION`;
    if (!g.Operator) g.Operator = "all";
    if (!g.Order) g.Order = "high";
    if (!g.Value || !Array.isArray(g.Value)) g.Value = [];
    return g;
  };

  // 1️⃣ Hooks at top
  const [group, setGroup] = useState(initializeGroup(condition));

  if (!condition) return null;

  // Update top-level field
  const handleChange = (key, value) => {
    const updated = { ...group, [key]: value };
    setGroup(updated);
    onUpdate(sectionKey, updated);
  };

  // Update a specific Value item
  const updateValueItem = (index, key, value) => {
    const newValue = [...(group.Value || [])];
    if (!newValue[index])
      newValue[index] = { Field: "", Operator: "==", Value: "", Order: "base" };
    newValue[index][key] = value;
    const updated = { ...group, Value: newValue };
    setGroup(updated);
    onUpdate(sectionKey, updated);
  };

  // Add a base condition
  const addCondition = () => {
    const newValue = [
      ...(group.Value || []),
      { Field: "", Operator: "==", Value: "", Order: "base" },
    ];
    const updated = { ...group, Value: newValue };
    setGroup(updated);
    onUpdate(sectionKey, updated);
  };

  // Add a nested group recursively
  const addGroup = () => {
    const newGroup = { Field: "", Operator: "", Value: [], Order: "high" };
    const newValue = [...(group.Value || []), newGroup]; // always push at the end
    const updated = { ...group, Value: newValue };
    setGroup(updated);
    onUpdate(sectionKey, updated);
  };

  // Remove an item
  const removeValueItem = (index) => {
    const newValue = [...(group.Value || [])];
    newValue.splice(index, 1);
    const updated = { ...group, Value: newValue };
    setGroup(updated);
    onUpdate(sectionKey, updated);
  };

  // Prepare grouped options for dropdowns
  const groupedOptions = buildGroupedOptions(mainSchemaData.CONFIG);
  const flatOptions = groupedOptions.flatMap((group) => group.options || []);
  return (
    <VStack
      border="1px solid #ddd"
      my={1}
      borderRadius="md"
      p={3}
      spacing={3}
      align="stretch"
    >
      {/* Header */}
      <Flex justify="space-between" align="center">
        <Box fontWeight="bold">Condition</Box>
        <DeleteIcon
          onClick={() => onDelete(sectionKey)}
          color="red"
          cursor="pointer"
        />
      </Flex>

      {/* Top-level fields */}
      <Flex gap={2} wrap="wrap">
        <Input
          type="hidden"
          size="sm"
          value={group.Field || ""}
          onChange={(e) => handleChange("Field", e.target.value)}
        />
        <Input
          size="sm"
          value={group.Order || "high"}
          onChange={(e) => handleChange("Order", e.target.value)}
          type="hidden"
        />
        <Box w="300px">
          <Select
            size="sm"
            value={group.Operator || "all"}
            onChange={(e) => handleChange("Operator", e.target.value)}
          >
            <option value="all">All</option>
            <option value="any">Any</option>
          </Select>
        </Box>
      </Flex>

      {/* Nested conditions */}
      {(group.Value || []).map((item, index) => {
        const isGroup = item.Order === "high" || Array.isArray(item.Value);
        if (isGroup) {
          return (
            <Box
              key={index}
              position="relative"
              border="1px dashed #aaa"
              p={2}
              borderRadius="md"
              mt={2}
            >
              <ConditionEditor
                sectionKey={sectionKey}
                condition={initializeGroup(item)}
                onUpdate={(sKey, updated) => {
                  const newValue = [...group.Value];
                  newValue[index] = updated;
                  const updatedGroup = { ...group, Value: newValue };
                  setGroup(updatedGroup);
                  onUpdate(sectionKey, updatedGroup);
                }}
                onDelete={() => removeValueItem(index)}
                mainSchemaData={mainSchemaData}
                fieldKey={fieldKey}
              />
            </Box>
          );
        }

        // Base condition
        return (
          <Flex key={index} gap={2} align="center">
            <Box w="400px">
              <ReactSelect
                options={groupedOptions}
                placeholder="Select Field"
                value={
                  flatOptions.find((opt) => opt.value === item.Field) || null
                }
                onChange={(selectedOption) => {
                  // selectedOption can be null if cleared
                  updateValueItem(
                    index,
                    "Field",
                    selectedOption ? selectedOption.value : ""
                  );
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </Box>
            <Box>
              <Select
                size="sm"
                value={item.Operator || "=="}
                onChange={(e) =>
                  updateValueItem(index, "Operator", e.target.value)
                }
              >
                <option value="==">is equal to</option>
                <option value="!=">is not equal to</option>
                <option value=">">is greater than</option>
                <option value="<">is less than</option>
                <option value=">=">is greater or equal</option>
                <option value="<=">is less or equal</option>
                <option value="in">is in</option>
                <option value="contains">contains</option>
              </Select>
            </Box>

            <Box>
              <Input
                size="sm"
                value={item.Value || ""}
                onChange={(e) =>
                  updateValueItem(index, "Value", e.target.value)
                }
              />
            </Box>
            <IconButton
              icon={<DeleteIcon />}
              size="xs"
              colorScheme="red"
              onClick={() => removeValueItem(index)}
            />
          </Flex>
        );
      })}

      {/* Add buttons at current level */}
      <Flex gap={2} mt={2}>
        <Button size="sm" leftIcon={<AddIcon />} onClick={addCondition}>
          Add Condition
        </Button>
        <Button size="sm" leftIcon={<AddIcon />} onClick={addGroup}>
          Add Group
        </Button>
      </Flex>
    </VStack>
  );
}
